import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useUserStore } from '@/store'
import { clearAuthStorage, hasValidStoredSession } from '@/utils/auth'
import { getUserMenus, getUserPermissions } from '@/api/menu'
import type { MenuVO } from '@/types/menu'

const componentMap: Record<string, () => Promise<any>> = {
  Dashboard: () => import('@/views/Dashboard.vue'),
  'asin/AsinList': () => import('@/views/asin/AsinList.vue'),
  'asin/AsinDetail': () => import('@/views/asin/AsinDetail.vue'),
  'alert/AlertList': () => import('@/views/alert/AlertList.vue'),
  'alert/AlertDetail': () => import('@/views/alert/AlertDetail.vue'),
  'monitor/ExecutionCenter': () => import('@/views/monitor/ExecutionCenter.vue'),
  'rule/RuleList': () => import('@/views/rule/RuleList.vue'),
  'rule/RuleConfig': () => import('@/views/rule/RuleConfig.vue'),
  'counter/CounterStrategy': () => import('@/views/counter/CounterStrategy.vue'),
  'marketplace/MarketplaceConfig': () => import('@/views/marketplace/MarketplaceConfig.vue'),
  'marketplace/CapacityConfig': () => import('@/views/marketplace/CapacityConfig.vue'),
  'monitor/ScheduleFix': () => import('@/views/monitor/ScheduleFix.vue'),
  'monitor/SchedulerDashboard': () => import('@/views/monitor/SchedulerDashboard.vue'),
  'monitor/OperationLog': () => import('@/views/monitor/OperationLog.vue'),
  'system/SensitiveWordManagement': () => import('@/views/system/SensitiveWordManagement.vue'),
  'system/RoleManagement': () => import('@/views/system/RoleManagement.vue'),
  'system/UserManagement': () => import('@/views/system/UserManagement.vue'),
  'system/DeptManagement': () => import('@/views/system/DeptManagement.vue'),
  'system/MenuManagement': () => import('@/views/system/MenuManagement.vue'),
  'system/Profile': () => import('@/views/system/Profile.vue'),
  'inventory/Overview': () => import('@/views/inventory/Overview.vue'),
  'inventory/Import': () => import('@/views/inventory/Import.vue'),
  'inventory/Ledger': () => import('@/views/inventory/Ledger.vue'),
  'inventory/SiteSummary': () => import('@/views/inventory/SiteSummary.vue'),
  'inventory/Warning': () => import('@/views/inventory/Warning.vue'),
  'inventory/InboundList': () => import('@/views/inventory/InboundList.vue'),
  'inventory/OutboundList': () => import('@/views/inventory/OutboundList.vue'),
  'inventory/OrderList': () => import('@/views/inventory/OrderList.vue'),
  'inventory/ProductBaseList': () => import('@/views/inventory/ProductBaseList.vue'),
  'inventory/InboundAnalysis': () => import('@/views/inventory/InboundAnalysis.vue'),
  'inventory/OutboundAnalysis': () => import('@/views/inventory/OutboundAnalysis.vue'),
  'inventory/OrderAnalysis': () => import('@/views/inventory/OrderAnalysis.vue'),
  'inventory/TurnoverAnalysis': () => import('@/views/inventory/TurnoverAnalysis.vue'),
  'inventory/ReportCenter': () => import('@/views/inventory/ReportCenter.vue'),
  'inventory/ImageList': () => import('@/views/inventory/ImageList.vue'),
  'inventory/SkuModelMapping': () => import('@/views/inventory/SkuModelMapping.vue'),
  'inventory/AmazonOrderList': () => import('@/views/inventory/AmazonOrderList.vue'),
  'inventory/ProductionSuggestion': () => import('@/views/inventory/ProductionSuggestion.vue'),
  'inventory/Cockpit': () => import('@/views/inventory/Cockpit.vue'),
  'lingxing/LingxingStore': () => import('@/views/lingxing/LingxingStore.vue'),
  'lingxing/LingxingOrders': () => import('@/views/lingxing/LingxingOrders.vue'),
  'lingxing/LingxingProductPerformance': () => import('@/views/lingxing/LingxingProductPerformance.vue'),
  'lingxing/ProfitPerformance': () => import('@/views/lingxing/ProfitPerformance.vue'),
  'lingxing/GrossProfit': () => import('@/views/lingxing/GrossProfit.vue'),
  'lingxing/CostTargetConfig': () => import('@/views/lingxing/CostTargetConfig.vue'),
  'lingxing/InventorySales': () => import('@/views/lingxing/InventorySales.vue'),
  'lingxing/DailySource': () => import('@/views/lingxing/DailySource.vue'),
  'lingxing/DailyPivot': () => import('@/views/lingxing/DailyPivot.vue'),
  'lingxing/SyncCenter': () => import('@/views/lingxing/SyncCenter.vue'),
  'lingxing/PipelineMonitor': () => import('@/views/lingxing/PipelineMonitor.vue'),
  'lingxing/ProductOverview': () => import('@/views/lingxing/ProductOverview.vue'),
  'lingxing/DashboardView': () => import('@/views/lingxing/DashboardView.vue'),
  'lingxing/StoreAnalysis': () => import('@/views/lingxing/StoreAnalysis.vue'),
  'lingxing/CategoryAnalysis': () => import('@/views/lingxing/CategoryAnalysis.vue'),
  'lingxing/OperationCockpit': () => import('@/views/lingxing/OperationCockpit.vue'),
  'lingxing/PerformanceReport': () => import('@/views/lingxing/PerformanceReport.vue'),
  'lingxing/PerformanceReportNew': () => import('@/views/lingxing/performanceCenter/PerformanceReportCenter.vue'),
  'lingxing/InventoryAnalysis': () => import('@/views/lingxing/InventoryAnalysis.vue'),
  'lingxing/LocalInventoryCenter': () => import('@/views/lingxing/LocalInventoryCenter.vue'),
  'dag/DagMonitor': () => import('@/views/dag/DagMonitor.vue'),
}

const staticRoutePaths = new Set(['/', '/dashboard'])

const staticRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { title: '\u767b\u5f55', public: true },
  },
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { title: '\u76d1\u63a7\u5927\u76d8' },
  },
  {
    path: '/lingxing/performance-report',
    redirect: '/lingxing/PerformanceReportNew',
  },
  {
    path: '/asins/:id(\\d+)',
    name: 'AsinDetail',
    component: componentMap['asin/AsinDetail'],
    meta: { title: 'ASIN\u8be6\u60c5', permissionCode: 'monitor:asin:list' },
  },
  {
    path: '/alerts/:id(\\d+)',
    name: 'AlertDetail',
    component: componentMap['alert/AlertDetail'],
    meta: { title: '\u544a\u8b66\u8be6\u60c5', permissionCode: 'alert:record:list' },
  },
  {
    path: '/rules/create',
    name: 'RuleCreate',
    component: componentMap['rule/RuleConfig'],
    meta: { title: '\u65b0\u5efa\u89c4\u5219', permissionCode: 'monitor:rule:list' },
  },
  {
    path: '/rules/:id(\\d+)/edit',
    name: 'RuleEdit',
    component: componentMap['rule/RuleConfig'],
    meta: { title: '\u7f16\u8f91\u89c4\u5219', permissionCode: 'monitor:rule:list' },
  },
  {
    path: '/inventory/inbound/:id(\\d+)/edit',
    name: 'InventoryInboundEdit',
    component: () => import('@/views/inventory/InboundEdit.vue'),
    meta: { title: '\u7f16\u8f91\u5165\u5e93', permissionCode: 'inventory:ledger:edit' },
  },
  {
    path: '/inventory/outbound/:id(\\d+)/edit',
    name: 'InventoryOutboundEdit',
    component: () => import('@/views/inventory/OutboundEdit.vue'),
    meta: { title: '\u7f16\u8f91\u51fa\u5e93', permissionCode: 'inventory:ledger:edit' },
  },
  {
    path: '/inventory/order/:id(\\d+)/edit',
    name: 'InventoryOrderEdit',
    component: () => import('@/views/inventory/OrderEdit.vue'),
    meta: { title: '\u7f16\u8f91\u8ba2\u5355', permissionCode: 'inventory:ledger:edit' },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: componentMap['system/Profile'],
    meta: { title: '\u4e2a\u4eba\u4e2d\u5fc3' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { title: '\u9875\u9762\u4e0d\u5b58\u5728' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes: staticRoutes,
  scrollBehavior() {
    return { top: 0 }
  },
})

function buildRoutesFromMenus(menus: MenuVO[]): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = []
  for (const menu of menus) {
    if (menu.type === 'C' && menu.status === 1 && menu.component) {
      if (menu.path && staticRoutePaths.has(menu.path)) {
        if (menu.children && menu.children.length > 0) {
          routes.push(...buildRoutesFromMenus(menu.children))
        }
        continue
      }
      const componentLoader = componentMap[menu.component]
      if (componentLoader) {
        routes.push({
          path: menu.path || `/menu-${menu.id}`,
          name: `menu-${menu.id}`,
          component: componentLoader,
          meta: {
            title: menu.name,
            menuId: menu.id,
            permissionCode: menu.permissionCode,
          },
        })
      }
    }
    if (menu.children && menu.children.length > 0) {
      routes.push(...buildRoutesFromMenus(menu.children))
    }
  }
  return routes
}

router.beforeEach(async (to, _from, next) => {
  document.title = to.meta.title
    ? `${to.meta.title} - \u4f5b\u5c71\u6c47\u7f8e\u5ead\u7535\u5546\u76d1\u63a7\u7cfb\u7edf`
    : '\u4f5b\u5c71\u6c47\u7f8e\u5ead\u7535\u5546\u76d1\u63a7\u7cfb\u7edf'

  const hasSession = hasValidStoredSession()

  if (to.meta.public) {
    if (to.path === '/login' && hasSession) {
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : '/'
      next(redirect)
      return
    }
    next()
    return
  }

  if (!hasSession) {
    clearAuthStorage()
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  const userStore = useUserStore()

  if (!userStore.dynamicRoutesLoaded) {
    try {
      const [menus, permissions] = await Promise.all([
        getUserMenus(),
        getUserPermissions(),
      ])

      userStore.setPermissions(permissions || [])
      userStore.setMenus(menus || [])

      const dynamicRoutes = buildRoutesFromMenus(menus || [])
      for (const route of dynamicRoutes) {
        if (!router.hasRoute(route.name as string)) {
          router.addRoute(route)
        }
      }

      userStore.setDynamicRoutesLoaded(true)
      next({ path: to.path, query: to.query, hash: to.hash, replace: true })
    } catch {
      clearAuthStorage()
      next({ path: '/login' })
    }
    return
  }

  const requiredPermission = typeof to.meta.permissionCode === 'string'
    ? to.meta.permissionCode
    : ''
  if (requiredPermission && !userStore.permissions.includes(requiredPermission)) {
    next({ name: 'NotFound' })
    return
  }

  // 首次登录（导入用户）强制修改密码：仅允许访问个人中心，其余页面一律重定向
  if (userStore.userInfo?.passwordResetRequired && to.path !== '/profile') {
    next({ path: '/profile', query: { forceChangePassword: '1' }, replace: true })
    return
  }

  next()
})

export default router
